import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Forsiden" },
  { to: "/artikler", label: "Artikler" },
  { to: "/opskrifter", label: "Opskrifter" },
  { to: "/video", label: "Video" },
  { to: "/det-sker", label: "Det sker" },
  { to: "/om", label: "Om" },
] as const;


/**
 * Site-nav med præcis 7 punkter (matcher designreference).
 * Desktop: centreret række med hårlinje øverst + 3px double linje under.
 * Mobil: burger-trigger + fuldskærms-sheet med store serif-linjer.
 */
export function SiteNav() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      {/* Desktop nav */}
      <nav
        aria-label="Hovednavigation"
        className="hidden md:block content-canvas"
      >
        <ul className="flex justify-center gap-10 py-5 mt-7 border-t border-rule border-b-[3px] border-b-double border-b-ink">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                activeOptions={{ exact: true }}
                activeProps={{ className: "nav-link-active" }}
                inactiveProps={{ className: "nav-link" }}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Mobil trigger */}
      <div className="md:hidden content-canvas">
        <div className="flex justify-center py-4 border-t border-rule border-b-[3px] border-b-double border-b-ink">
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Åbn menu"
            className="inline-flex items-center gap-2 font-serif text-[11px] font-medium tracking-[0.16em] uppercase text-ink"
          >
            <span className="inline-flex flex-col gap-[3px] w-[15px]" aria-hidden="true">
              <span className="h-[1.5px] bg-current" />
              <span className="h-[1.5px] bg-current" />
              <span className="h-[1.5px] bg-current" />
            </span>
            Menu
          </button>
        </div>
      </div>

      {/* Mobil fuldskærms-sheet */}
      <div
        aria-hidden={!open}
        onClick={() => setOpen(false)}
        className={`md:hidden fixed inset-0 z-40 bg-ink/35 transition-opacity duration-300 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        role="dialog"
        aria-label="Menu"
        aria-hidden={!open}
        className={`md:hidden fixed inset-0 z-50 bg-paper transition-transform duration-[340ms] ease-out flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(.4,0,.2,1)" }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-rule">
          <span className="font-serif text-[26px] leading-none">Menu</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Luk menu"
            className="text-ink-soft hover:text-ink transition-colors"
          >
            <X size={22} strokeWidth={1.5} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-6 py-4">
          <ul className="divide-y divide-rule">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  activeOptions={{ exact: true }}
                  onClick={() => setOpen(false)}
                  className="block py-4 font-serif text-[24px] leading-tight text-ink hover:text-accent transition-colors"
                  activeProps={{ className: "block py-4 font-serif text-[24px] leading-tight text-accent" }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
}
